import { useEffect, useMemo, useState } from 'react';
import { exportEmailLogs, getEmailLog, listEmailLogs, retryEmailLog } from '../../../api/adminEmailCenter';
import EmailCenterPage, {
  EmptyState,
  PageSection,
  StatCard,
  StatusBadge,
  TextField,
  downloadCsv,
  formatDateTime,
} from './EmailCenterShared';

export default function EmailMailLogs() {
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    messageType: '',
    sourceModule: '',
    recipientEmail: '',
    from: '',
    to: '',
  });
  const [state, setState] = useState({
    loading: true,
    error: '',
    items: [],
    pagination: { page: 1, totalPages: 1 },
    summary: {},
  });
  const [activeLog, setActiveLog] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [retryingId, setRetryingId] = useState(null);

  const load = async (page = 1) => {
    setState((current) => ({ ...current, loading: true, error: '' }));
    try {
      const data = await listEmailLogs({ page, limit: 20, ...filters });
      setState({
        loading: false,
        error: '',
        items: data.items || [],
        pagination: data.pagination || { page: 1, totalPages: 1 },
        summary: data.summary || {},
      });
    } catch (error) {
      setState((current) => ({ ...current, loading: false, error: error.message || 'Failed to load email logs' }));
    }
  };

  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const summaryCards = useMemo(
    () => [
      { label: 'Sent', value: state.summary.sent || 0, tone: 'emerald' },
      { label: 'Failed', value: state.summary.failed || 0, tone: 'rose' },
      { label: 'Queued', value: state.summary.queued || 0, tone: 'amber' },
      { label: 'Retried', value: state.summary.retried || 0, tone: 'sky' },
      { label: 'Today Sent', value: state.summary.todaySent || 0, tone: 'slate' },
      { label: 'Last 7 Days', value: state.summary.last7DaysSent || 0, tone: 'slate' },
    ],
    [state.summary]
  );

  const openDetail = async (id) => {
    setDetailLoading(true);
    try {
      const data = await getEmailLog(id);
      setActiveLog(data.item);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleExport = async () => {
    const csv = await exportEmailLogs(filters);
    downloadCsv(csv, `email-logs-${new Date().toISOString().slice(0, 10)}.csv`);
  };

  const handleRetry = async (id) => {
    setRetryingId(id);
    try {
      await retryEmailLog(id);
      await load(state.pagination.page || 1);
      if (activeLog?.id === id) {
        await openDetail(id);
      }
    } catch (error) {
      setState((current) => ({ ...current, error: error.message || 'Retry failed' }));
    } finally {
      setRetryingId(null);
    }
  };

  return (
    <EmailCenterPage
      title="Mail Logs"
      description="Server-side filtered delivery history with message details, provider response, retry actions, and campaign linkage."
      actions={
        <button type="button" onClick={handleExport} className="rounded-full border border-slate-200 px-5 py-3 text-sm font-semibold text-slate-700">
          Export CSV
        </button>
      }
    >
      <div className="grid gap-4 xl:grid-cols-6 md:grid-cols-3">
        {summaryCards.map((card) => (
          <StatCard key={card.label} label={card.label} value={card.value} tone={card.tone} />
        ))}
      </div>

      {state.error ? <div className="rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-700">{state.error}</div> : null}

      <PageSection
        title="Delivery Grid"
        subtitle="Filter by recipient, source module, status, date range, or message type and open any row for full timeline details."
        action={
          <div className="grid gap-3 md:grid-cols-4">
            <TextField label="Search" value={filters.search} onChange={(e) => setFilters((current) => ({ ...current, search: e.target.value }))} />
            <label className="block">
              <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Status</span>
              <select value={filters.status} onChange={(e) => setFilters((current) => ({ ...current, status: e.target.value }))} className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-900">
                <option value="">All</option>
                <option value="queued">Queued</option>
                <option value="processing">Processing</option>
                <option value="sent">Sent</option>
                <option value="failed">Failed</option>
                <option value="skipped">Skipped</option>
              </select>
            </label>
            <TextField label="Message Type" value={filters.messageType} onChange={(e) => setFilters((current) => ({ ...current, messageType: e.target.value }))} />
            <TextField label="Source Module" value={filters.sourceModule} onChange={(e) => setFilters((current) => ({ ...current, sourceModule: e.target.value }))} />
            <TextField label="Recipient Email" value={filters.recipientEmail} onChange={(e) => setFilters((current) => ({ ...current, recipientEmail: e.target.value }))} />
            <TextField label="From" type="date" value={filters.from} onChange={(e) => setFilters((current) => ({ ...current, from: e.target.value }))} />
            <TextField label="To" type="date" value={filters.to} onChange={(e) => setFilters((current) => ({ ...current, to: e.target.value }))} />
            <div className="self-end">
              <button type="button" onClick={() => load(1)} className="rounded-full bg-slate-900 px-5 py-3 text-sm font-semibold text-white">
                Apply
              </button>
            </div>
          </div>
        }
      >
        {!state.items.length && !state.loading ? (
          <EmptyState title="No mail logs match the current filters" body="Once transactional or campaign emails are sent, they will appear here with retry and provider details." />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-[0.24em] text-slate-500">
                <tr>
                  <th className="px-4 py-3">Recipient</th>
                  <th className="px-4 py-3">Subject</th>
                  <th className="px-4 py-3">Campaign</th>
                  <th className="px-4 py-3">Source</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Attempts</th>
                  <th className="px-4 py-3">Sent</th>
                  <th className="px-4 py-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {state.items.map((row) => (
                  <tr key={row.id} className="border-t border-slate-100">
                    <td className="px-4 py-4">
                      <p className="font-semibold text-slate-900">{row.recipientName || 'Guest'}</p>
                      <p className="text-slate-500">{row.recipientEmail}</p>
                    </td>
                    <td className="px-4 py-4 text-slate-700">{row.subject}</td>
                    <td className="px-4 py-4 text-slate-600">{row.campaignName || 'Direct send'}</td>
                    <td className="px-4 py-4 text-slate-600">
                      {row.sourceModule}
                      <div className="text-xs uppercase tracking-[0.18em] text-slate-400">{row.messageType}</div>
                    </td>
                    <td className="px-4 py-4">
                      <StatusBadge value={row.status} />
                    </td>
                    <td className="px-4 py-4 text-slate-600">{row.attemptCount}</td>
                    <td className="px-4 py-4 text-slate-600">{formatDateTime(row.sentAt)}</td>
                    <td className="px-4 py-4">
                      <div className="flex flex-wrap gap-2">
                        <button type="button" onClick={() => openDetail(row.id)} className="rounded-full border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700">
                          Details
                        </button>
                        {['failed', 'rejected'].includes(row.status) ? (
                          <button
                            type="button"
                            disabled={retryingId === row.id}
                            onClick={() => handleRetry(row.id)}
                            className="rounded-full bg-emerald-600 px-3 py-2 text-xs font-semibold text-white"
                          >
                            {retryingId === row.id ? 'Retrying...' : 'Retry'}
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </PageSection>

      {activeLog || detailLoading ? (
        <div className="fixed inset-0 z-50 flex items-stretch justify-end bg-slate-950/50">
          <div className="h-full w-full max-w-2xl overflow-y-auto bg-white p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-slate-100 pb-4">
              <div>
                <p className="text-[11px] uppercase tracking-[0.24em] text-emerald-700">Mail Detail</p>
                <h2 className="mt-2 text-2xl font-semibold text-slate-900">{activeLog?.subject || 'Loading...'}</h2>
              </div>
              <button type="button" onClick={() => setActiveLog(null)} className="rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700">
                Close
              </button>
            </div>
            {detailLoading || !activeLog ? (
              <div className="py-8 text-sm text-slate-500">Loading details...</div>
            ) : (
              <div className="space-y-5 py-5">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="rounded-[24px] border border-slate-100 p-4">
                    <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Recipient</p>
                    <p className="mt-2 font-semibold text-slate-900">{activeLog.recipientName || 'Guest'}</p>
                    <p className="text-sm text-slate-500">{activeLog.recipientEmail}</p>
                  </div>
                  <div className="rounded-[24px] border border-slate-100 p-4">
                    <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Status</p>
                    <div className="mt-2">
                      <StatusBadge value={activeLog.status} />
                    </div>
                    <p className="mt-3 text-sm text-slate-500">Attempt count: {activeLog.attemptCount}</p>
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="rounded-[24px] border border-slate-100 p-4 text-sm text-slate-600">
                    <p><strong>Campaign:</strong> {activeLog.campaignName || 'Direct send'}</p>
                    <p className="mt-2"><strong>Template:</strong> {activeLog.templateName || 'Custom'}</p>
                    <p className="mt-2"><strong>Source Module:</strong> {activeLog.sourceModule}</p>
                    <p className="mt-2"><strong>SMTP Message ID:</strong> {activeLog.smtpMessageId || 'N/A'}</p>
                  </div>
                  <div className="rounded-[24px] border border-slate-100 p-4 text-sm text-slate-600">
                    <p><strong>Created:</strong> {formatDateTime(activeLog.createdAt)}</p>
                    <p className="mt-2"><strong>Last Attempt:</strong> {formatDateTime(activeLog.lastAttemptAt)}</p>
                    <p className="mt-2"><strong>Sent:</strong> {formatDateTime(activeLog.sentAt)}</p>
                    <p className="mt-2"><strong>Failure:</strong> {activeLog.failureReason || 'N/A'}</p>
                  </div>
                </div>

                <PageSection title="Delivery Timeline" subtitle="Every queue, attempt, send, fail, and retry event captured for auditability.">
                  <div className="space-y-3">
                    {(activeLog.timeline || []).map((event) => (
                      <div key={event.id} className="rounded-[24px] border border-slate-100 bg-slate-50/60 p-4">
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-3">
                            <StatusBadge value={event.status} />
                            <span className="text-sm font-semibold text-slate-900">{event.eventType}</span>
                          </div>
                          <span className="text-xs uppercase tracking-[0.18em] text-slate-400">{formatDateTime(event.createdAt)}</span>
                        </div>
                        {event.detailText ? <p className="mt-3 text-sm text-slate-600">{event.detailText}</p> : null}
                        {event.providerResponse ? <pre className="mt-3 overflow-x-auto rounded-2xl bg-slate-900 p-3 text-xs text-slate-100">{event.providerResponse}</pre> : null}
                      </div>
                    ))}
                  </div>
                </PageSection>
              </div>
            )}
          </div>
        </div>
      ) : null}
    </EmailCenterPage>
  );
}
